import { createRef, useEffect, useRef, useState } from "react"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { cssVarExpansion, cssVarHeight, cssVarTransitionDelay, cssVarTransitionDuration } from "contexts/Header"
import { useNavigation } from "contexts/Navigation"
import { useViewport } from "contexts/Viewport"
import { useLocale } from "contexts/Locale"

import getMediaQuery from "css/breakpoints"
import { buttonFilled, buttonIcon, opaqueGreen, translucentDark } from "css/buttons"
import { fullGrid } from "css/grid"
import { bold, light } from "css/text"

import useSSR from "hooks/useSSR"
import useScrollLock from "hooks/useScrollLock"

import { ISimpButton } from "components/quickviews/IButton"
import { Icon } from "components/icon/Icon"
import { useWatchgrid } from "./context"
import { getOriginLevel2, sendWatchGridEvent } from "./events"
import { addParam, getAllParams, getCurrentUrl, removeParam, setParam } from "./utils"

const Root = styled.div`
  --subnav-ofs: calc(var(--subnav-height) * var(--is-sub));
  align-content: start;

  @media (max-width: 1024px) {
    ${fullGrid}
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgb(var(--pure-white));
    z-index: 1000;
    overflow-y: auto;

    .panel-open & {
      display: grid;
    }
  }

  ${getMediaQuery("l")} {
    grid-area: sidebar;
    width: 0;
    overflow: hidden;

    .panel-open & {
      width: auto;
      overflow: unset;
    }
  }
`
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 0;
  width: 100%;
  box-sizing: border-box;
  padding-block: 6vh 20px;
  margin-block: 0 20px;
  background: rgb(var(--pure-white));
  z-index: 2;
  grid-column: main;

  ${getMediaQuery("m")} {
    grid-column: col 2 / span 10;
  }

  ${getMediaQuery("l")} {
    padding-inline: 40px 0;
    padding-block: 20px;
    margin-block: -20px 0;
    top: calc((var(${cssVarHeight}) * var(${cssVarExpansion}) + var(--subnav-ofs) + 80px));
    transition: top var(${cssVarTransitionDuration}) var(${cssVarTransitionDelay});
  }
`
const Reset = styled.button`
  ${buttonFilled}
  ${translucentDark}
`
const Close = styled.button`
  ${buttonIcon}
  ${translucentDark}
`
const Content = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  box-sizing: border-box;
  grid-column: main;
  display: grid;

  ${getMediaQuery("m")} {
    grid-column: col 2 / span 10;
  }

  ${getMediaQuery("l")} {
    top: calc((var(${cssVarHeight}) * var(${cssVarExpansion}) + var(--subnav-ofs) + 80px + 84px + 20px));
    height: calc(100vh - (var(${cssVarHeight}) * var(${cssVarExpansion})) - var(--subnav-ofs) - 80px - 84px - 20px);
    transition: top var(${cssVarTransitionDuration}) var(${cssVarTransitionDelay});
    padding-inline: 40px 0;
    margin-block-start: 20px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`
const Wrapper = styled.div`
  padding-block-end: 160px;

  ${getMediaQuery("m")} {
    column-count: 2;
  }

  ${getMediaQuery("l")} {
    column-count: unset;
    padding: unset;
  }
`
const Fieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;

  ${getMediaQuery("m")} {
    break-inside: avoid;
    margin-block-end: 40px;
    padding-inline-end: var(--grid-col-unit);
  }

  ${getMediaQuery("l")} {
    padding-inline-end: unset;
  }
`
const LegendButtonContainer = styled.div`
  font-size: 20px;
  line-height: 1.4;
  ${bold}
  padding-block: 12px;
  border-bottom: 1px solid rgb(var(--grey));
  display: flex;
  align-items: center;
  position: relative;

  .info-btn {
    margin-inline-end: auto;
    margin-inline-start: 8px;
    position: relative;
    z-index: 1;
  }

  > button:not(.info-btn) {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: end;
    align-items: center;
  }
`
const LegendButton = styled.button`
  color: inherit;
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  display: flex;
`
const ButtonIcon = styled.span`
  --iconSize: 12px;
  --iconStrokeWidth: 2px;

  height: var(--iconSize);
  position: relative;
  width: var(--iconSize);
  pointer-events: none;

  ::before,
  ::after {
    background: currentColor;
    content: "";
    display: block;
    height: var(--iconStrokeWidth);
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transition: transform 0.6s;
    will-change: transform;

    html.prefers-reduced-motion & {
      transition: none;
    }
  }

  ::after {
    transform: rotate(90deg);
  }

  .active & {
    ::after {
      transform: rotate(0);
    }
  }

  .no-js & {
    display: none;
  }
`
const Legend = styled.legend`
  font-size: 20px;
  line-height: 1.4;
  ${bold}
  display: block;
  width: 100%;
  padding-block-end: 12px;
  margin-block-end: 20px;
  border-bottom: 1px solid rgb(var(--grey));

  ${getMediaQuery("l")} {
    font-size: 18px;
  }

  & button {
    margin-inline-start: 8px;
  }
`
const ListWrapper = styled.div`
  height: 0;
  overflow: hidden;
  transition: height 0.5s;

  ${getMediaQuery("m")} {
    height: auto;
    overflow: unset;
  }
`
const List = styled.div`
  padding-block: 20px;

  ${getMediaQuery("m")} {
    padding-block: 0;
  }
`
const Item = styled.div`
  position: relative;
  margin-block-end: 15px;

  &:last-of-type {
    margin: 0;
  }
`
const Label = styled.label`
  font-size: 16px;
  line-height: 1.4;
  ${light}
  cursor: pointer;
  user-select: none;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  box-sizing: border-box;

  &:before {
    content: "";
    width: 16px;
    height: 16px;
    margin-inline-end: 10px;
    border: 1px solid rgb(0 0 0);
    transition: background 0.3s, border 0.3s;
  }

  &.label-checkbox:before {
    border-radius: 25%;
  }

  &.label-radio:before {
    border-radius: 50%;
  }

  ${getMediaQuery("l")} {
    font-size: 14px;
  }

  @media (hover: hover) {
    &:hover {
      color: rgb(var(--green));

      &:before {
        border-color: rgb(var(--green));
      }
    }
  }
`
const Input = styled.input`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  opacity: 0;
  transform: translate(-100%);

  &:checked + label {
    color: rgb(var(--green));
  }

  &:checked + .label-checkbox:before {
    color: rgb(var(--pure-white));
    background-color: rgb(var(--green));
    border-color: rgb(var(--green));
    background-image: url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 height='15' width='15' viewBox='0 0 15 15' fill='white'%3E%3Cpath d='M5.4,11.9L1.6,7.5l1.5-1.3l2.5,2.9L12,3.7l1.3,1.5L5.4,11.9z'%3E%3C/path%3E%3C/svg%3E");
    background-position: center center;

    [dir="rtl"] & {
      transform: scaleX(-1);
    }
  }

  &:checked + .label-radio {
    &:before {
      // color: rgb(var(--pure-white));
      // background-color: rgb(var(--green));
      // border-color: rgb(var(--green));
      // background-image: url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 height='15' width='15' viewBox='0 0 15 15' fill='white'%3E%3Cpath d='M5.4,11.9L1.6,7.5l1.5-1.3l2.5,2.9L12,3.7l1.3,1.5L5.4,11.9z'%3E%3C/path%3E%3C/svg%3E");
      // background-position: center center;

      border-color: rgb(var(--green));
    }

    &:after {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      content: "";
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: rgb(var(--green));

      [dir="ltr"] & {
        left: 3px;
      }

      [dir="rtl"] & {
        right: 3px;
      }
    }
  }

  &:disabled + label {
    color: rgb(var(--grey));
    cursor: unset;

    &:before {
      border-color: rgb(var(--grey));
      background: rgb(var(--grey));
    }
  }

  &.focus-visible + label {
    outline: 2px solid rgb(var(--focus, 0 255 255) / 1);
    outline-offset: 3px;
    margin-inline: 5px;
  }
`
const Bottom = styled.div`
  grid-column: main;
  display: flex;
  justify-content: center;
  position: fixed;
  top: 80vh;
  left: 0;
  z-index: 5;
  width: 100%;

  ${getMediaQuery("l")} {
    display: none;
  }
`
const NoResult = styled.button`
  ${buttonFilled}
  ${translucentDark}
`
const ResultsButton = styled.button`
  ${buttonFilled}
  ${opaqueGreen}
`
const FocusLooper = styled.span`
  position: absolute;
`

export default function AdvancedFilters({ gridData, gridFilters, closeMethod, searchHubs, pipelines, searchUids }) {
  const { advanced_filters } = gridFilters

  const bodyElement = process.browser && document.body

  const { advancedPanel, setFilterStrip } = useWatchgrid()
  const viewport = useViewport()
  const scrollLock = useScrollLock()
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const ssr = useSSR()
  const env = useEnv()
  const locale = useLocale()
  const console = useConsole()

  const currentParams = getAllParams()

  const [breakpoint, setBreakpoint] = useState(viewport.bp.current)
  const [, setAccordion] = useState(null)

  const resetRef = useRef()

  const onFilterChanged = e => {
    const { type, name, value } = e.target

    const currentUrl = getCurrentUrl()

    const params = currentUrl.searchParams

    let action = null
    if (type === "radio") {
      setParam(name, value, params)
    } else {
      if (currentParams[name]?.includes(value)) {
        removeParam(name, value, params)
        action = "remove"
      } else {
        addParam(name, value, params)
        action = "add"
      }
    }
    const validArray = Array.from(params).filter(v => !["group", "sort"].includes(v[0]))
    setFilterStrip(validArray.length > 0)

    // coveo event
    searchUids.forEach((searchUid, index) => {
      const token = pipelines[index]

      sendWatchGridEvent(env.organizationId, token, "search", {
        actionCause: action === "remove" ? "facetDeselect" : "facetSelect",
        language: locale?.current?.codes?.www,
        numberOfResults: gridData.length,
        queryText: "",
        responseTime: 0,
        searchQueryUid: searchUid,
        searchHub: searchHubs[index],
        anonymous: true,
        originLevel1: searchHubs[index],
        originLevel2: getOriginLevel2(currentParams?.global_category?.[0]),
        userAgent: navigator?.userAgent,
        customData: {
          facetField: name,
          facetValue: value,
          facetId: "family",
          facetTitle: "collection",
        },
      })
    })

    navigation.push(currentUrl.href, { nopageview: true }, { scroll: false })

    if (Array.from(params).length === 0) setFilterStrip(false)
  }

  const onReset = () => {
    setFilterStrip(false)

    // coveo events
    searchUids.forEach((searchUid, index) => {
      const token = pipelines[index]

      sendWatchGridEvent(env.organizationId, token, "search", {
        actionCause: "facetClearAll",
        language: locale?.current?.codes?.www,
        numberOfResults: gridData.length,
        queryText: "",
        responseTime: 0,
        searchQueryUid: searchUid,
        searchHub: searchHubs[index],
        anonymous: true,
        originLevel1: searchHubs[index],
        originLevel2: getOriginLevel2(currentParams?.global_category?.[0]),
        userAgent: navigator?.userAgent,
      })
    })

    const currentUrl = getCurrentUrl()

    const navigationUrl = `${currentUrl.origin}${currentUrl.pathname}${
      currentUrl.searchParams.get("sort") ? `?sort=${currentUrl.searchParams.get("sort")}` : ""
    }`

    navigation.push(navigationUrl, { nopageview: true }, { scroll: false })
  }

  const onClose = () => {
    closeMethod()
  }

  useEffect(() => {
    if (advancedPanel && (viewport.bp.current === "s" || viewport.bp.current === "m")) scrollLock.lock(bodyElement)
    else scrollLock.unlock(bodyElement)
  }, [advancedPanel, viewport.bp.current, scrollLock, bodyElement])

  const onBPChange = () => {
    setBreakpoint(viewport.bp.current)
  }
  useEffect(() => viewport.bp.onChange(onBPChange))

  const onAccordionChange = (e, i) => {
    setAccordion(i)

    e.target.classList.toggle("active")

    const buttonNode = buttonRefs.current[i].current
    const listNode = listRefs.current[i].current

    let blockHeight = 0
    let ariaExpanded = false
    if (listNode.offsetHeight === 0) {
      ariaExpanded = true
      listNode.hidden = false
      blockHeight = `${listNode.querySelector("div").offsetHeight}px`
      listNode.style.height = blockHeight
    } else {
      listNode.style.height = blockHeight
      setTimeout(() => {
        listNode.hidden = true
      }, 500)
    }

    buttonNode.setAttribute("aria-expanded", ariaExpanded)
  }

  const focusLoop = () => {
    resetRef.current.focus()
  }

  const buttonRefs = useRef([])
  buttonRefs.current = advanced_filters.map((_, i) => buttonRefs.current[i] ?? createRef())

  const listRefs = useRef([])
  listRefs.current = advanced_filters.map((_, i) => listRefs.current[i] ?? createRef())

  console.log("• advanced_filters", advanced_filters)

  return (
    <Root role='dialog' aria-modal='true' style={{ "--is-sub": !ssr && document.querySelector(".header-subnav") ? 1 : 0 }} aria-label={dictionary.filters()}>
      <Top>
        <Reset ref={resetRef} className='advancedFiltersReset' onClick={onReset}>
          <Icon type='reset' />
          {dictionary.reset()}
        </Reset>

        <Close onClick={onClose} aria-label={dictionary.ariaLabelCloseFiltersPanel()}>
          <Icon type='close' />
        </Close>
      </Top>

      <Content>
        <Wrapper>
          {advanced_filters.map((group, i) => {
            return (
              <Fieldset key={group.facet}>
                {breakpoint === "s" || ssr ? (
                  <LegendButtonContainer>
                    {group.localizedFacet}
                    {group.hasTerm && (
                      <ISimpButton
                        list={advanced_filters
                          .find(x => x.facet === group.facet)
                          .values.map(({ value, localizedValue }) => ({ techCode: value, topicLabel: localizedValue }))}
                        feature={group.facet}
                        gridData={gridData}
                      />
                    )}
                    <LegendButton
                      type='button'
                      ref={buttonRefs.current[i]}
                      aria-expanded='false'
                      aria-controls={`advancedfilters-panel-${i}`}
                      onClick={e => onAccordionChange(e, i)}
                    >
                      <ButtonIcon />
                    </LegendButton>
                  </LegendButtonContainer>
                ) : (
                  <Legend>
                    <span id='filterCategory'>{group.localizedFacet}</span>
                    {group.hasTerm && (
                      <ISimpButton
                        list={advanced_filters
                          .find(x => x.facet === group.facet)
                          .values.map(({ value, localizedValue }) => ({ techCode: value, topicLabel: localizedValue }))}
                        feature={group.facet}
                        gridData={gridData}
                        ariaDescribedby='filterCategory'
                      />
                    )}
                  </Legend>
                )}

                <ListWrapper
                  id={`advancedfilters-panel-${i}`}
                  ref={listRefs.current[i]}
                  style={{ height: ssr || breakpoint !== "s" ? "auto" : null }}
                  hidden={ssr || breakpoint === "s"}
                >
                  <List>
                    {group.values.map((filter, j) => {
                      let checkedTest = currentParams[filter.facet]?.includes(filter.value) || false

                      if (group.type === "radio" && !currentParams[filter.facet] && j === 0) {
                        checkedTest = true
                      }

                      return (
                        <Item key={filter.value}>
                          <Input
                            type={group.type}
                            id={`advancedfilters-${filter.facet}-${group.type}-${j}`}
                            name={filter.facet}
                            value={filter.value}
                            onChange={onFilterChanged}
                            checked={checkedTest && filter.quantity > 0}
                            disabled={!group.allowMultipleChoice && filter.quantity === 0}
                          />
                          <Label htmlFor={`advancedfilters-${filter.facet}-${group.type}-${j}`} className={`label-${group.type}`}>
                            {filter.localizedValue}
                          </Label>
                        </Item>
                      )
                    })}
                  </List>
                </ListWrapper>
              </Fieldset>
            )
          })}
        </Wrapper>
      </Content>

      {!ssr && breakpoint !== "l" && (
        <Bottom>
          {gridData.length === 0 ? (
            <NoResult className='dark-theme'>{dictionary.noResultButton()}</NoResult>
          ) : (
            <ResultsButton onClick={onClose}>{dictionary.results()}</ResultsButton>
          )}
        </Bottom>
      )}

      <FocusLooper tabIndex='0' onFocus={focusLoop} />
    </Root>
  )
}
